import statusService from "api/statusService";
import tagsService from "api/tagsService";
import CaseHeader from "components/CaseHeader";
import CaseView from "components/CaseView";
import CasesContext from "contexts/CasesContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import styles from "./fullscreen-styles.module.scss";
import PDFViewer from "components/PDFViewer";
import usePDF from "hooks/usePDF";
import { ItemKind } from "dto/IKindItems";
import Comments from "components/Comments";
import KeysContext from "contexts/KeysContext";
import useGetPDFViewer from "hooks/useGetPDFViewer";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import EmptyPanel from "components/EmptyPanel";
import PanelsLayout from "components/PanelsLayout";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import PackagesContext from "contexts/PackagesContext";
import DossierFilesContext from "contexts/DossierFilesContext";
import { replaceFileExtension } from "utils/replaceFileExtension";

const CasesFullscreen = () => {
  const { caseId } = useParams();
  const { data: tagsData } = tagsService.useGetTags();
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const { data: statusesData } = statusService.useGetStatuses();
  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const [, setAllwaysShowRightSide] =
    useState<boolean>(true);

  const { packages } = useContext(PackagesContext);
  const { dossierFiles } = useContext(DossierFilesContext);

  const { options } = useContext(settingsContext);

  useEffect(() => {
    if (options) {
      const casesView = options.find(
        (x) => x.Type === IOptionType.caseInNewTabView
      );

      if (casesView) {
        setAllwaysShowRightSide(casesView.Value === "fullscreen");
      }
    }
  }, [options]);

  const { cases } = useContext(CasesContext);

  const caseContext = useMemo(
    () => cases.find((c) => c.Id === caseId) ?? null,
    [caseId, cases]
  );

  const { blob, removeBlob, create } =
    usePDF();

  const optionsRef = useRef<HTMLDivElement>();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const PDFId = queryParams.get("pdf");

  const { getAnnotationToAdd, setAnnotationToAdd } = useGetPDFViewer();

  useEffect(() => {
    if (showComments || !getAnnotationToAdd()) return;
    setShowComments(true);
  }, [getAnnotationToAdd()]);

  useContext(KeysContext);

  useEffect(() => {
    //if (!keys.length) return;
    if (!PDFId) {
      if (blob) {
        removeBlob();
      }
      return;
    }

    const splittedPDFId = PDFId.split(":");

    const packId = splittedPDFId[0];
    const fileName = splittedPDFId[1];
    const fileUri = splittedPDFId[2];
    const fileKind = splittedPDFId[3];
    const fileEncryptionKey = splittedPDFId[4];

    console.log(
      "Preview in case",
      fileName,
      fileUri,
      fileKind,
      fileEncryptionKey
    );

    const attach = packages
      .reduce((a, v) => a.concat(v.Attachments ?? []), [])
      .find(
        (attachment) =>
          attachment.Uri === fileUri ||
          attachment.Id === fileUri ||
          attachment.Id === packId
      );

    if (attach) {
      create(fileUri, attach, Number(fileKind) as ItemKind);
    } else {
      const di = dossierFiles.find(
        (d) => d.FileUri == fileUri || d.SignedPdfUri == fileUri
      );

      if (di) {
        create(
          di.AttachmentParentId ?? fileUri,
          {
            Id: di.DossierId,
            Uri: di.SignedPdfUri ? di.SignedPdfUri : di.FileUri,
            FileName: di.SignedPdfUri
              ? replaceFileExtension(di.FileName, ".pdf")
              : di.FileName,
            ContentType: di.FileContentType,
            Format: di.FileContentType,
            Kind: Number(fileKind),
            Label: di.Label,
            Size: di.FileSize,
            CommentsCount: di.CommentsCount,
            CreationDate: di.CreationDate,
            DeskInfo_IsOn: false,
            DossierId: di.DossierId,
            ShowInDossier: 0,
            Type: DossierItemType.Attachment,
            LastModified: di.LastModified,
            IsShared: di.IsShared,
            TagCount: di.TagCount,
            EncryptionKeyId: fileEncryptionKey,
            Keys: [],
            ObjectKeys: [],
          },
          Number(fileKind) as ItemKind
        );
      } else if (Number(fileKind) === ItemKind.CourtPortalDeliverance) {
        create(
          packId,
          {
            Id: packId,
            Uri: null,
            FileName: fileName,
            ContentType: "application/pdf",
            Format: ".pdf",
            Kind: Number(fileKind),
            Label: "",
            Size: 0,
            CommentsCount: 0,
            CreationDate: null,
            DeskInfo_IsOn: false,
            DossierId: packId,
            ShowInDossier: 0,
            Type: DossierItemType.Attachment,
            LastModified: null,
            IsShared: false,
            TagCount: 0,
            EncryptionKeyId: fileEncryptionKey,
            Keys: [],
            ObjectKeys: [],
          },
          ItemKind.CourtPortalDeliveranceDocument as ItemKind
        );
      }
    }
  }, [PDFId, location, dossierFiles]);

  const [showComments, setShowComments] = useState(false);

  const leftPanel = () => {
    return (
      <>
        <CaseView showCase hideHeader statuses={statuses} tags={tags} />
      </>
    );
  };

  const rightPanel = () => {
    return (
      <>
        {!blob && <EmptyPanel></EmptyPanel>}
        {blob && (
          <PDFViewer
            setShowComments={() => setShowComments(!showComments)}
            file={blob ?? undefined}
          />
        )}
      </>
    );
  };

  return (
    <div className={styles.wrapper} ref={optionsRef}>
      <div className={styles.container}>
        <CaseHeader optionsRef={optionsRef} Case={caseContext} />
        <div className={`${styles.viewContainer}`}>
          <PanelsLayout
            layoutName="casesFullView"
            onChangeSearch={() => {}}
            disableHeader={true}
            defaultLeft={35}
            minLeft={30}
            leftPanel={leftPanel()}
            rightPanel={rightPanel()}
            resize
            leftClassName={styles.leftFull}
            allowRightPanelOverflow={true}
            trackActivePanel={true}
          />
        </div>
      </div>
      {showComments && (
        <div
          className={`${styles.commentsWrapper} ${
            showComments ? styles.show : ""
          }`}
        >
          <Comments
            toggle={() => {
              setShowComments(false);
              setAnnotationToAdd(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CasesFullscreen;
